import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ThreadHistoryContext, toggleArchivedInHistory, togglePinnedInHistory } from './helpers';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { LoadState } from '@/lib/helpers';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { getAllThreads } from '@/lib/services/thread.service';

export const ThreadHistoryProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const { threadHistory, updateThreadHistory, isLoading: settingsLoading } = useUserSettings();

  const {
    data: allThreads,
    isLoading: threadsLoading,
    isSuccess: threadLoadSuccess,
  } = useQuery([QueryKey.ThreadsList], async () => getAllThreads());

  const isLoading = settingsLoading || threadsLoading;

  const toggleArchived = useCallback(
    (threadId: string) => {
      updateThreadHistory((prev) => toggleArchivedInHistory(prev, threadId));
    },
    [updateThreadHistory]
  );

  const togglePinned = useCallback(
    (threadId: string) => {
      updateThreadHistory((prev) => togglePinnedInHistory(prev, threadId));
    },
    [updateThreadHistory]
  );

  useEffect(() => {
    if (loadState !== 'unloaded' || isLoading || !threadLoadSuccess) {
      return;
    }

    const validThreadIds = new Set((allThreads ?? []).map(({ id }) => id));
    const validThreadHistory = threadHistory.filter(({ threadId }) => validThreadIds.has(threadId));

    if (validThreadHistory.length < threadHistory.length) {
      updateThreadHistory(() => validThreadHistory);
    }

    setLoadState('loading');
  }, [loadState, isLoading, threadLoadSuccess, allThreads, threadHistory, updateThreadHistory]);

  return (
    <ThreadHistoryContext.Provider
      value={{
        threadHistory,
        toggleArchived,
        togglePinned,
        isLoading,
      }}
    >
      {children}
    </ThreadHistoryContext.Provider>
  );
};
